import React, { useEffect } from "react";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";

import { Box, VStack, IconButton } from "@chakra-ui/react";
import { AddIcon, MinusIcon } from "@chakra-ui/icons";

import { CctvCamera } from "redux/services/cctv/types";

import "leaflet/dist/leaflet.css";
import { useCameraLocationName } from "hooks/useCameraNameLocation";

// marker for cctv
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

interface CctvMapProps {
    cameras: CctvCamera[];
    selectedCamera?: CctvCamera;
    onMarkerClick?: (camera: CctvCamera) => void;
    height?: string;
    center?: [number, number];
    zoom?: number;
}

const ZoomControl: React.FC = () => {
    const map = useMap();

    return (
        <VStack
            position="absolute"
            top="10px"
            right="10px"
            boxShadow="md"
            zIndex={1000}
            spacing={0}
            borderRadius="md"
            overflow="hidden"
            border="1px solid"
            borderColor="gray.200"
        >
            <IconButton
                aria-label="Zoom in"
                icon={<AddIcon />}
                size="sm"
                bg="white"
                color="gray.700"
                borderRadius="0"
                _hover={{ bg: "gray.50" }}
                borderBottomWidth="1px"
                borderBottomColor="gray.100"
                onClick={() => map.zoomIn()}
            />
            <IconButton
                aria-label="Zoom out"
                icon={<MinusIcon />}
                size="sm"
                bg="white"
                color="gray.700"
                borderRadius="0"
                _hover={{ bg: "gray.50" }}
                onClick={() => map.zoomOut()}
            />
        </VStack>
    );
};

const MapUpdater: React.FC<{ center: [number, number]; zoom: number; trigger: any }> = ({ center, zoom, trigger }) => {
    const map = useMap();
    const lastTrigger = React.useRef(trigger);

    useEffect(() => {
        if (trigger !== lastTrigger.current) {
            map.setView(center, zoom);
            lastTrigger.current = trigger;
        }
    }, [center, zoom, map, trigger]);

    return null;
};

const CctvMap: React.FC<CctvMapProps> = ({
    cameras,
    selectedCamera,
    onMarkerClick,
    height = "500px",
    center = [-7.797068, 110.370529],
    zoom = 13,
}) => {
    const mapCenter = React.useMemo(() => {
        return selectedCamera?.lat && selectedCamera?.long
            ? [selectedCamera.lat, selectedCamera.long] as [number, number]
            : center;
    }, [selectedCamera?.lat, selectedCamera?.long, center]);

    const mapZoom = React.useMemo(() => {
        return selectedCamera ? 16 : zoom;
    }, [selectedCamera, zoom]);

    // Create custom icon for active/offline cameras
    const createCustomIcon = (isOnline: boolean) => {
        const color = isOnline ? "#54B558" : "#F4F4F4";
        const svgColor = isOnline ? "#fff" : "#CCCCCC";
        return L.divIcon({
            className: "custom-marker",
            html: `<div style="
                background-color: ${color};
                color: ${svgColor};
                width: 32px;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 30%;
                ">
                    <img src="/icons/cctv-icon.svg" alt="CCTV Icon" style={{ width: "100%", height: "100%" }} />

      </div>`,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
        });
    };

    const {CameraLocationName} = useCameraLocationName();

    return (
        <Box h={height} w="100%" borderRadius="md" overflow="hidden" boxShadow="sm" position="relative" zIndex={0}>
            <MapContainer
                center={center}
                zoom={zoom}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={true}
                zoomControl={false}
            >
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <ZoomControl />
                <MapUpdater center={mapCenter} zoom={mapZoom} trigger={selectedCamera?.id} />
                {cameras
                    .filter((cam) => cam.lat && cam.long)
                    .map((camera) => (
                        <Marker
                            key={camera.id}
                            position={[camera.lat!, camera.long!]}
                            icon={createCustomIcon(camera.connection === 1)}
                            eventHandlers={{
                                click: () => onMarkerClick?.(camera),
                            }}
                        >
                            <Popup>
                                <strong>{CameraLocationName(camera.name)}</strong>
                                <br />
                                {camera.location}
                                <br />
                                Status: {camera.connection === 1 ? "Online" : "Offline"}
                            </Popup>
                        </Marker>
                    ))}
            </MapContainer>
        </Box>
    );
};

export default CctvMap;
